import { Component, OnInit } from '@angular/core';

import { AzavistaApiService, IEvent, IStage } from '@azavista/servicelib';
import { AbstractWidgetDirective, IWidgetComponentDefinition } from '@azavista/components/widgets';
import { IField } from '@azavista/components/shared';
import { ParticipantsByStageWidgetService } from './participants-by-stage-widget.service';
import { SharedService, IWidgetDataChanges, TranslationKey } from '../../../shared';
import { IParticipantsByStageSettings } from './interfaces';
import { EventsService } from '../../../events/events.service';
import { WidgetsUtilsService } from '../widgets-utils.service';
import { WidgetViewDetailsNavigationPaths } from '../interfaces';
import { WidgetCustomEventType } from '../enums';
import { IDataPointSelectedArguments } from '../apexcharts-wrapper/interfaces';
import { CommonDialogsService } from '../../common-dialogs.service';
import { ChartType } from '../apextypes';

@Component({
    templateUrl: 'participants-by-stage-widget.component.html',
    styleUrls: ['participants-by-stage-widget.component.css'],
    standalone: false
})
export class ParticipantsByStageWidgetComponent extends AbstractWidgetDirective implements OnInit {
    chart: ApexChart = {
        type: 'donut'
    };
    series: ApexAxisChartSeries | ApexNonAxisChartSeries;
    title: ApexTitleSubtitle;
    labels: string[];
    allEventParticipantFields: IField[];
    enumTypeEventParticipantFields: IField[];
    definitionSettings: IParticipantsByStageSettings;
    columns: IField[];
    isEventPredefined = false;
    event: IEvent;
    eventNotAvailable = false;
    stages: IStage[];

    private eventId = '';

    constructor(
        private apiSvc: AzavistaApiService,
        private participantsByStageSvc: ParticipantsByStageWidgetService,
        private sharedSvc: SharedService,
        private eventsSvc: EventsService,
        private widgetsUtilsSvc: WidgetsUtilsService,
        private commonDlgsSvc: CommonDialogsService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.definitionSettings = this.definition.settings as IParticipantsByStageSettings;
        this.definition.data = null;

        this.eventId = this.definition.context?.eventId;
        try {
            if (this.eventId) {
                // The widget is inside event context
                await this.setEventFromContext(this.eventId);
                this.eventNotAvailable = false;
            } else {
                this.eventId = this.definition.settings.remote.event_id;
            }
        } catch (err) {
            this.eventNotAvailable = true;
            return;
        }
        this.setCustomWidgetParams();

        this.chart = {
            type: this.definitionSettings && this.definitionSettings.local.chartType
                ? this.definitionSettings.local.chartType
                : this.chart.type,
            toolbar: { show: false },
            zoom: { enabled: false },
            height: 200,
            animations: { enabled: true },
        };

        super.subscribeToDataChanges({
            next: (newData?: IWidgetDataChanges) => {
                this.dataChanged(newData);
            }
        });

        if (this.configMode) {
            if (this.eventId) {
                // Initially add only the selected event as item in event autocomplete
                try {
                    this.event ||= await this.widgetsUtilsSvc.getEvent(this.eventId);
                    this.eventNotAvailable = false;
                    this.setStages(this.eventId);
                } catch (err) {
                    this.eventNotAvailable = true;
                }
            }
            this.setDefaultRemoteSettingsIfMissing();
            this.onSettingsChanged();
        } else {
            const hasRemoteSettings = !this.widgetsUtilsSvc.areRemoteSettingsEmpty(this.definitionSettings.remote);
            if (hasRemoteSettings) {
                // Start loading on next loop because it causes loading output to be emitted which could cause
                // Angular change detection errors in the consumer (the dashboard component) if it changes template bound variables
                // in the event handler
                window.setTimeout(() => this.loadWidgetData(this.definition));
            } else {
                // Notify parent that we need remote settings
                this.settingsNeeded.next();
            }
        }

        if (!this.definitionSettings.local.chartType) {
            this.definitionSettings.local.chartType = this.chart.type as any;
        }
        if (!this.definitionSettings.local.display_stages) {
            const stages = await this.getStages(this.eventId);
            this.definitionSettings.local.display_stages = stages.map(x => x.id);
        }
        const { series, labels } = this.convertDataToSeriesAndLabels(this.definition.data);
        this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
        this.labels = labels;
    }

    async getStages(eventId: string): Promise<IStage[]> {
        if (!eventId) {
            return [];
        }
        return await this.apiSvc.getAllStages('event', eventId);
    }

    async setStages(eventId: string): Promise<void> {
        this.stages = await this.getStages(eventId);
    }

    onDataPointSelected(args: IDataPointSelectedArguments): void {
        this.customEvent.emit({ type: WidgetCustomEventType.dataPointSelected, data: args });
    }

    setCustomWidgetParams(): void {
        this.definition.viewButtonText = this.sharedSvc.translate(TranslationKey.goToEvent);
        this.definition.viewDetailsNavPath = WidgetViewDetailsNavigationPaths.event;
        this.definition.viewDetailsNavQueryParams = this.widgetsUtilsSvc.getViewDetailsNavQueryParamsForEvent(this.eventId);
        this.onSettingsChanged();
    }

    async setEventFromContext(contextEventId: string): Promise<void> {
        this.isEventPredefined = true;
        this.definitionSettings.remote.event_id = contextEventId;
        this.event = await this.widgetsUtilsSvc.getEvent(contextEventId);
    }

    async onSelectEvent(): Promise<void> {
        const title = this.sharedSvc.translate(TranslationKey.events);
        const dlgResult = await this.commonDlgsSvc.showSelectEventsDialog(title, true);
        if (!dlgResult || dlgResult.length === 0) {
            return;
        }
        this.event = dlgResult[0] as IEvent;
        const eventId = this.event ? this.event.id : null;
        this.definitionSettings.remote.event_id = eventId;
        this.eventId = eventId;
        this.setCustomWidgetParams();
        this.onSettingsChanged();
        setTimeout(() => this.setStages(this.eventId));
    }

    async setEventsData(): Promise<void> {
        await this.eventsSvc.loadGlobalEventFields();
        this.columns = this.sharedSvc.clone(this.eventsSvc.getEventsColumns(true));
    }

    setDefaultRemoteSettingsIfMissing(): void {
        if (!this.definitionSettings.remote) {
            this.definitionSettings.remote = { event_id: this.eventId };
        }
    }

    loadWidgetData(definition: IWidgetComponentDefinition): void {
        this.definitionSettings = definition.settings;
        this.eventNotAvailable = !(definition.settings as IParticipantsByStageSettings)?.remote?.event_id;
        if (definition.size?.height) {
            this.chart.height = definition.size.height;
            this.chart.width = definition.size.width;
        }
        this.processDataLoaded(definition);
    }

    async processDataLoaded(definition: IWidgetComponentDefinition): Promise<void> {
        try {
            this.notifyLoading(true);
            definition.data = await this.getStages(this.definition.settings.remote.event_id);
            this.eventNotAvailable = false;
            definition.dataChangedSubject.next({ data: definition.data });
        } catch (err) {
            this.eventNotAvailable = true;
        } finally {
            this.notifyLoading(false);
        }
    }

    processDataLoadedError(): void {
        // TODO: What if report data fails ?
        this.notifyLoading(false);
    }

    convertDataToSeriesAndLabels(data: IStage[]): { series: number[], labels: string[] } {
        if (!data) {
            return {
                labels: [],
                series: []
            };
        }
        const series: number[] = [];
        const labels: string[] = [];
        if (data.length) {
            data.forEach(stage => {
                const displayStages = this.definition.settings.local.display_stages || this.stages.map(x => x.id) || [];
                if (displayStages.includes(stage.id)) {
                    series.push(stage.entities_in_stage);
                    labels.push(stage.name);
                }
            });
        }

        return {
            series: series,
            labels: labels
        };
    }

    displayStagesChanged(): void {
        this.onSettingsChanged();
    }

    chartTypeChanged(newType: string): void {
        this.chart.type = newType as ChartType;
        const { series, labels } = this.convertDataToSeriesAndLabels(this.definition.data);
        this.labels = labels;
        this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
    }

    dataChanged(newData?: IWidgetDataChanges): void {
        let chartTypeChanged = false;
        let shouldConvertSeries = false;

        if (newData.settings && newData.settings.local) {
            if (this.chart.type !== newData.settings.local.chartType) {
                this.chart.type = newData.settings.local.chartType;
                chartTypeChanged = true;
            }
        }
        if (newData.data) {
            shouldConvertSeries = true;
        }
        if (newData.width && this.chart.width !== newData.width) {
            this.chart.width = newData.width;
        }
        if (newData.height && this.chart.height !== newData.height) {
            this.chart.height = newData.height;
        }
        if (chartTypeChanged || shouldConvertSeries) {
            const { series, labels } = this.convertDataToSeriesAndLabels(newData.data);
            this.series = this.widgetsUtilsSvc.convertSeries(this.chart.type, series);
            this.labels = labels;
        }
        if (chartTypeChanged) {
            this.chartTypeChanged(newData.settings.local.chartType);
        }
        if (!this.configMode && newData.settings && newData.settings.remote) {
            // Not config mode and remote settings were changed - reload data from server
            this.loadWidgetData(this.definition);
        }
    }

    onSettingsChanged(): void {
        const settingsAreValid = this.participantsByStageSvc.areSettingsValid(this.definitionSettings);
        this.settingsChanged.next({
            definition: this.definition,
            settings: this.definitionSettings,
            settingsAreValid: settingsAreValid
        });
    }
}

